import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getMyDeliveries,
    subscribeToMyDeliveryUpdates,
    isDeliveryCompleted
} from "../../lib/supabase/my-deliveries";

import {
    FiCalendar, FiUser, FiPhone, FiMapPin, FiAlertCircle, FiEye,
    FiMail, FiPhoneOff, FiClock, FiCheckCircle, FiTruck,
    FiFlag, FiSearch, FiPackage, FiHome, FiLayers, FiBox, FiEdit3, FiInfo
} from "react-icons/fi";
import { uuidToShortId } from "../../lib/common/shortId";

const setMobileViewport = () => {
    let viewport = document.querySelector("meta[name=viewport]");

    if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
    }

    viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );
};

const resetViewport = () => {
    const viewport = document.querySelector("meta[name=viewport]");

    if (viewport) {
        viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1"
        );
    }
};

const MyDeliveries = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    const [deliveries, setDeliveries] = useState([]);
    const [filteredDeliveries, setFilteredDeliveries] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const STORAGE_KEY = "notify_my_deliveries_filters";

    const getSavedFilters = () => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        } catch {
            return {};
        }
    };

    const savedFilters = getSavedFilters();

    const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm || "");
    const [sortBy, setSortBy] = useState(savedFilters.sortBy || "assign_date");
    const [showCompleted, setShowCompleted] = useState(savedFilters.showCompleted ?? false);

    useEffect(() => {
        setMobileViewport();

        return () => {
            resetViewport();
        };
    }, []);

    useEffect(() => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                searchTerm,
                sortBy,
                showCompleted,
            })
        );
    }, [searchTerm, sortBy, showCompleted]);

    useEffect(() => {
        if (company?.id && userProfile?.id) {
            fetchMyDeliveries();
        }
    }, [company, userProfile]);

    const fetchMyDeliveries = async () => {
        try {
            const data = await getMyDeliveries(userProfile.id, company.id);
            setDeliveries(data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching my deliveries:", error);
            alert("Error fetching your deliveries");
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToMyDeliveryUpdates(company.id, (payload) => {
            fetchMyDeliveries();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id]);

    // Filter and search logic
    useEffect(() => {
        const getTime = (value, fallback = 0) => {
            const time = value ? new Date(value).getTime() : fallback;
            return Number.isFinite(time) ? time : fallback;
        };

        const getDeliveryDate = (delivery) =>
            delivery.delivery?.delivery_date ||
            delivery.quote_info?.expected_delivery_date ||
            delivery.created_at;

        const getSearchText = (delivery) => {
            return [
                delivery.unique_id,
                delivery.short_id,
                delivery.quote_info?.child_unique_id,
                delivery.quote_info?.quote_type,
                delivery.customer_name,
                delivery.customer_number,
                delivery.customer_mail,
                delivery.customer_address,
                delivery.delivery?.delivery_address,
                delivery.delivery?.delivery_contact_name,
                delivery.delivery?.delivery_contact_phone,
                delivery.delivery?.delivery_status,
                delivery.quote_info?.delivery_time_slot,
                delivery.quote_info?.delivery_note,
                delivery.delivery?.special_instructions,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
        };

        let next = [...deliveries];

        const term = searchTerm.trim().toLowerCase();

        if (term) {
            next = next.filter((delivery) =>
                getSearchText(delivery).includes(term)
            );
        }

        if (!showCompleted) {
            next = next.filter((delivery) => !isDeliveryCompleted(delivery.delivery));
        }

        next.sort((a, b) => {
            switch (sortBy) {
                case "assign_date": {
                    const aDate = getTime(a.delivery?.assigned_at || a.created_at);
                    const bDate = getTime(b.delivery?.assigned_at || b.created_at);
                    return bDate - aDate;
                }

                case "newest": {
                    return getTime(b.created_at) - getTime(a.created_at);
                }

                case "oldest": {
                    return getTime(a.created_at) - getTime(b.created_at);
                }

                case "delivery_date": {
                    return getTime(getDeliveryDate(a), Number.MAX_SAFE_INTEGER) -
                        getTime(getDeliveryDate(b), Number.MAX_SAFE_INTEGER);
                }

                case "urgent": {
                    const aUrgent = a.urgent_quote || a.urgent_design ? 1 : 0;
                    const bUrgent = b.urgent_quote || b.urgent_design ? 1 : 0;

                    if (aUrgent !== bUrgent) return bUrgent - aUrgent;

                    return getTime(getDeliveryDate(a), Number.MAX_SAFE_INTEGER) -
                        getTime(getDeliveryDate(b), Number.MAX_SAFE_INTEGER);
                }

                default:
                    return 0;
            }
        });

        setFilteredDeliveries(next);
    }, [deliveries, searchTerm, sortBy, showCompleted]);

    const getDeliveryStatusColor = (status) => {
        const statusColors = {
            pending: "bg-amber-50 text-amber-700 border-amber-200",
            in_transit: "bg-blue-50 text-blue-700 border-blue-200",
            delivered: "bg-green-50 text-green-700 border-green-200",
            completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
            cancelled: "bg-red-50 text-red-700 border-red-200"
        };
        return statusColors[status] || "bg-gray-50 text-gray-700 border-gray-200";
    };

    const getDeliveryStatusIcon = (status) => {
        const icons = {
            pending: <FiClock className="w-3.5 h-3.5" />,
            in_transit: <FiTruck className="w-3.5 h-3.5" />,
            delivered: <FiCheckCircle className="w-3.5 h-3.5" />,
            completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            cancelled: <FiAlertCircle className="w-3.5 h-3.5" />
        };
        return icons[status] || <FiClock className="w-3.5 h-3.5" />;
    };

    const getQuoteTypeIcon = (quoteType) => {
        const icons = {
            roof: <FiHome className="w-3 h-3" />,
            wall: <FiLayers className="w-3 h-3" />,
            floor: <FiBox className="w-3 h-3" />,
            timber: <FiPackage className="w-3 h-3" />
        };
        return icons[quoteType] || <FiPackage className="w-3 h-3" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatRelativeTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);
        const diffMs = now - inputDate;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'just now';
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return formatDate(date);
    };

    const handleViewDelivery = (deliveryCard) => {
        const deliveryId = deliveryCard?.delivery?.id;

        if (!deliveryId) {
            navigate(`/${companySlug}/assignment/${deliveryCard.job_id}`, {
                state: { from: location.pathname }
            });
            return;
        }

        const shortDeliveryId = uuidToShortId(deliveryId);

        navigate(`/${companySlug}/assignment/${shortDeliveryId}`, {
            state: {
                from: location.pathname,
                quoteId: deliveryCard.quote_info?.id,
                deliveryId,
                quoteType: deliveryCard.quote_info?.quote_type,
                deliveryCount: deliveryCard.delivery?.delivery_count ?? 0,
                childId: deliveryCard.delivery?.child_id || null,
            }
        });
    };

    const FilterButton = ({ label, isSelected, onClick }) => (
        <button
            type="button"
            onClick={onClick}
            className={`min-h-[44px] px-3 py-2 rounded-xl border transition-all duration-150 font-semibold text-sm [touch-action:manipulation] ${isSelected
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "bg-white text-gray-700 border-gray-300 active:bg-blue-50"
                }`}
        >
            {label}
        </button>
    );

    // Get quote type styling (color and badge)
    const getQuoteTypeStyle = (quoteType) => {
        switch (quoteType) {
            case 'roof':
                return { bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' };
            case 'wall':
                return { bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' };
            case 'floor':
                return { bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-200' };
            case 'timber':
                return { bgColor: 'bg-amber-100', textColor: 'text-amber-800', borderColor: 'border-amber-200' };
            default:
                return { bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-200' };
        }
    };

    const isQuoteDeliveryCompleted = (quoteCard) => {
        return isDeliveryCompleted(quoteCard.delivery);
    };

    const openNativeMaps = (address) => {
        if (!address) return;

        const encoded = encodeURIComponent(address);

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        if (isIOS) {
            window.location.href = `http://maps.apple.com/?q=${encoded}`;
            return;
        }

        if (isAndroid) {
            window.location.href = `geo:0,0?q=${encoded}`;
            return;
        }

        window.open(
            `https://www.google.com/maps/search/?api=1&query=${encoded}`,
            "_blank",
            "noopener,noreferrer"
        );
    };


    return (
        <div className="mobile-list-page driver-deliveries-page min-h-screen bg-gray-50 [touch-action:manipulation]">
            <div className="w-full max-w-4xl mx-auto sm:mx-0 sm:max-w-full">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FiTruck className="w-7 h-7 text-blue-600" />
                                My Deliveries
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">
                                Last updated {formatRelativeTime(lastUpdated)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by Job ID, Customer, or Address..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full min-h-[46px] pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm bg-white"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 min-h-[34px] px-2 text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg [touch-action:manipulation]"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-700">Sort:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="min-h-[44px] w-full px-3 py-2 border border-gray-300 rounded-xl text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white [touch-action:manipulation]"
                                >
                                    <option value="assign_date">Assign Date</option>
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="delivery_date">Delivery Date</option>
                                    <option value="urgent">Urgent First</option>
                                </select>
                            </div>

                            {/* Completion Filter */}
                            <div className="grid grid-cols-1 gap-2">
                                <span className="text-sm font-medium text-gray-700">Show:</span>
                                <FilterButton
                                    label="All Deliveries"
                                    isSelected={showCompleted}
                                    onClick={() => setShowCompleted(true)}
                                />
                                <FilterButton
                                    label="Hide Completed"
                                    isSelected={!showCompleted}
                                    onClick={() => setShowCompleted(false)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredDeliveries.length}</span> of{' '}
                            <span className="font-semibold text-gray-900">{deliveries.length}</span> deliveries
                        </span>
                    </div>
                </div>

                {/* Deliveries List */}
                <div className="space-y-3">
                    {filteredDeliveries.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiTruck className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No deliveries found</h3>
                            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        filteredDeliveries.map((quoteCard) => {
                            const quoteType = quoteCard.quote_info?.quote_type;
                            const childUniqueId = quoteCard.quote_info?.child_unique_id;
                            const quoteStyle = getQuoteTypeStyle(quoteType);
                            const isCompleted = isQuoteDeliveryCompleted(quoteCard);
                            const deliveryStatus = quoteCard.delivery?.delivery_status || 'scheduled';

                            const isCancelled = !!quoteCard.is_cancelled;
                            const displayStatus = isCancelled ? 'cancelled' : deliveryStatus;

                            return (
                                <div
                                    key={quoteCard.quote_info?.id || `${quoteCard.id}-${quoteType}-${quoteCard.delivery?.id}`}
                                    // onClick={() => handleViewDelivery(quoteCard)}
                                    className={`rounded-2xl border shadow-sm transition-all duration-150 overflow-hidden ${isCancelled
                                        ? "bg-red-50 border-red-300 opacity-80 cursor-not-allowed"
                                        : isCompleted
                                            ? "border-green-300 bg-green-50 active:scale-[0.99]"
                                            : "bg-white border-gray-200 active:scale-[0.99]"
                                        }`}
                                >
                                    <div className="p-4">
                                        {/* Header Row */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`font-semibold text-base flex items-center gap-1`}>
                                                        {childUniqueId
                                                            ? childUniqueId
                                                            : quoteType.charAt(0).toUpperCase() + quoteType.slice(1)
                                                        }
                                                    </span>
                                                    <span className={`text-gray-900 text-xs px-2 py-1 rounded flex items-center gap-1 ${quoteStyle.bgColor} ${quoteStyle.textColor}`}>
                                                        {getQuoteTypeIcon(quoteType)}
                                                        {quoteCard.unique_id}
                                                    </span>
                                                    {/* Quote / Child ID Badge */}

                                                    {(quoteCard.urgent_quote || quoteCard.urgent_design) && (
                                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                                                            <FiFlag className="w-3 h-3" />
                                                            URGENT
                                                        </span>
                                                    )}
                                                    {/* {isCompleted && (
                                                        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold flex items-center gap-1">
                                                            <FiCheckCircle className="w-3 h-3" />
                                                            Completed
                                                        </span>
                                                    )} */}
                                                </div>
                                            </div>

                                            <span className={`px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getDeliveryStatusColor(displayStatus)}`}>
                                                {getDeliveryStatusIcon(displayStatus)}
                                                {displayStatus.replace(/_/g, ' ').toUpperCase()}
                                            </span>
                                        </div>

                                        {/* Content Grid */}
                                        <div className="grid grid-cols-1 gap-3">
                                            {/* Customer Info */}
                                            <div className="md:col-span-3">
                                                <div className="flex items-start gap-2">
                                                    <FiUser className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="flex flex-row min-w-0 gap-4 items-center content-center">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {quoteCard.customer_name}
                                                        </p>
                                                        <div className="flex items-center gap-1.5 mt-1">
                                                            {quoteCard.customer_number && (
                                                                <a
                                                                    href={`tel:${quoteCard.customer_number}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onTouchEnd={(e) => e.stopPropagation()}
                                                                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 [touch-action:manipulation]"
                                                                >
                                                                    <FiPhone className="w-3 h-3" />
                                                                    Call
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Delivery Address */}
                                            <div className="md:col-span-4">
                                                <div className="flex items-start gap-2">
                                                    <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {quoteCard.delivery?.delivery_address || quoteCard.customer_address || "No address provided"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Delivery Date */}
                                            <div className="md:col-span-2">
                                                <div className="flex items-start gap-2">
                                                    <FiCalendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-gray-500">Delivery Date</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {formatDate(
                                                                quoteCard.delivery?.delivery_date ||
                                                                quoteCard.quote_info?.expected_delivery_date
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>


                                            {/* Delivery Information Section */}
                                            {(quoteCard.quote_info?.delivery_time_slot || quoteCard.quote_info?.delivery_note || quoteCard.delivery?.special_instructions) && (
                                                <div className="mt-0 pt-4 border-t border-gray-200">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {/* Time Slot */}
                                                        {quoteCard.quote_info?.delivery_time_slot && (
                                                            <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                                                <FiClock className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-medium text-purple-900 mb-0.5">Delivery Time Slot</p>
                                                                    <p className="text-sm font-semibold text-purple-700">
                                                                        {quoteCard.quote_info.delivery_time_slot} Delivery
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Delivery Note */}
                                                        {quoteCard.quote_info?.delivery_note && (
                                                            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                                <FiEdit3 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-medium text-blue-900 mb-0.5">Delivery Note</p>
                                                                    <p className="text-sm text-blue-700 whitespace-pre-wrap">
                                                                        {quoteCard.quote_info.delivery_note}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Special Instructions from delivery table */}
                                                        {quoteCard.delivery?.special_instructions && (
                                                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg md:col-span-2">
                                                                <FiInfo className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-medium text-amber-900 mb-0.5">Special Instructions</p>
                                                                    <p className="text-sm text-amber-700 whitespace-pre-wrap">
                                                                        {quoteCard.delivery.special_instructions}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <div className="md:col-span-3 flex items-center justify-end gap-2">
                                                {(quoteCard.delivery?.delivery_address || quoteCard.customer_address) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openNativeMaps(quoteCard.delivery?.delivery_address || quoteCard.customer_address);
                                                        }}
                                                        className="text-xs text-white flex items-center aspect-square h-full bg-green-600 rounded-xl [touch-action:manipulation]"
                                                    >
                                                        <FiMapPin className="w-[1.15rem] h-[1.15rem] self-center items-center justify-center m-auto" />
                                                    </button>
                                                )}
                                                <button
                                                    disabled={isCancelled}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isCancelled) return;
                                                        handleViewDelivery(quoteCard);
                                                    }}
                                                    onTouchEnd={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isCancelled) return;
                                                        handleViewDelivery(quoteCard);
                                                    }}
                                                    className={`w-full min-h-[44px] px-4 py-2 rounded-xl transition-colors text-sm font-semibold flex items-center justify-center gap-2 [touch-action:manipulation] ${isCancelled
                                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                        : "bg-blue-600 text-white active:bg-blue-700"
                                                        }`}
                                                >
                                                    <FiEye className="w-4 h-4" />
                                                    {isCancelled ? 'Job Cancelled' : 'View Delivery'}
                                                </button>
                                            </div>
                                        </div>


                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyDeliveries;
